import {
    Injectable,
    Signal,
    WritableSignal,
    signal,
    inject
} from '@angular/core';
import {
    IIqNtlResponse,
    IIqRateNtlStateService,
    IqNtlResponseSchema,
    NtlCountryType,
    NtlEquipType,
    NtlRateType,
    NtlTimeInterval,
    emptyNtlResponse,
    EquipmentTypes,
    RatesCache,
    RateInfo,
    RatesByType,
    RateTypes,
} from '../../models';
import { DatiqGraphqlApolloService } from '@dat/datiq-graphql';
import { ApolloQueryResult } from '@apollo/client/core';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import { firstValueFrom } from 'rxjs';
import { gql } from 'apollo-angular';
import { isNil, isEmpty } from 'lodash';

@Injectable({
    providedIn: 'root',
})

export class IqRateNtlStateService {
    private _apollo: DatiqGraphqlApolloService = inject(DatiqGraphqlApolloService);
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    equipmentType: WritableSignal<string> = signal(EquipmentTypes.Van);
    rateType: WritableSignal<string> = signal('');

    nationalRates: WritableSignal<RatesCache> = signal({
        errored: false,
        [EquipmentTypes.Van]: {} as RatesByType,
        [EquipmentTypes.Flatbed]: {} as RatesByType,
        [EquipmentTypes.Reefer]: {} as RatesByType,
    });

    constructor() {
        this._bus.registerMap({
            'type:rate-fetch:national-rates': {
                callback: ({ equipmentType }: { equipmentType: EquipmentTypes }) => {
                    const isFetched = !isEmpty(this.nationalRates()[equipmentType]);
                    
                    if (isFetched) return;

                    this.fetchNationalRates(equipmentType).then((rateInfo: RatesByType) => {
                        this.nationalRates.update(currentData => ({
                            ...currentData,
                            [equipmentType]: rateInfo,
                            errored: rateInfo.errored,
                        }));
                    });
                }
            }
        });
    }

    private fetchNationalRates(equipmentType: EquipmentTypes): Promise<RatesByType> {
        const contractRates = this._getRatesByType('Contract', equipmentType);
        const spotRates = this._getRatesByType('Spot', equipmentType);
        const fuelRates = this._getRatesByType('Fuel', equipmentType);

        return Promise.all([
            contractRates,
            spotRates,
            fuelRates
        ]).then(([_contractRates, _spotRates, _fuelRates]) => {
            if (
                _contractRates.errored ||
                _spotRates.errored || 
                _fuelRates.errored
            ) {
                return {
                    errored: true,
                    [RateTypes.Contract]: _contractRates.rates,
                    [RateTypes.Spot]: _spotRates.rates,
                    [RateTypes.Fuel]: _fuelRates.rates
                };
            }
            
            return {
                errored: false,
                [RateTypes.Contract]: _contractRates.rates,
                [RateTypes.Spot]: _spotRates.rates,
                [RateTypes.Fuel]: _fuelRates.rates
            };
        });
    }

    private _getRatesByType(
        rateType: string, //NtlRateType,
        equipmentType: EquipmentTypes, //NtlEquipType,
    ): Promise<{ errored: boolean; rates: IIqNtlResponse }> {
        return firstValueFrom(
            this._apollo.getApollo().query({
                query: gql`
                    query (
                        $query: RATESNATIONAL_SearchCriteriaInputRatesNational
                    ) {
                        national: getRatesNational(query: $query) {
                            changePercent
                            changeRate
                            isChangePositive
                            isChangeNegative
                            isChangeNeutral
                            rates {
                                mile {
                                    date
                                    average
                                }
                            }
                        }
                    }
                `,
                variables: {
                    query: {
                        equipmentCategory: equipmentType,
                        period: 'Week',
                        rateType: rateType,
                        country: 'US',
                    },
                },
            }),
        ).then((result: ApolloQueryResult<IIqNtlResponse>) => this._processResult(result));
    }

    private _processResult(
        result: ApolloQueryResult<IIqNtlResponse>,
    ): { errored: boolean; rates: IIqNtlResponse } {
        try {
            const parsedData: IIqNtlResponse = IqNtlResponseSchema.parse(result.data);
            const rates: IIqNtlResponse = parsedData;

            return {
                errored: false,
                rates
            };
        } catch (err) {
            return {
                errored: true,
                rates: {}
            }
        }
    }
}
